// Packages
import moment from 'moment-timezone'
// Helpers
import {
  Invoice,
} from '../utils/api'
import {
  stringifyObjectToQueryString,
} from './helpers'

const resultsPerPage = 25

export const configureSearchParams = (status, componentData, propsData, specialParams = {}) => {
  /*
   * status = invoice status
   * componentData = `this` in the component calling this function
   */

  // API Request (https://fs-bobtail.github.io/bobtail/#api-Invoice-SearchInvoices)
  // params are null if the user hasn't searched for anything
  // i.e. this covers the case a user just paginates the invoices from onload
  const params = {
    account_manager_id: componentData?.inputs?.accountManager
      ? componentData.inputs.accountManager.id
      : null,
    client_id: componentData?.inputs?.clientID || null,
    debtor_id: componentData?.inputs?.debtorID || null,
    display_id: componentData?.inputs?.invoiceID || null,
    load_number: componentData?.inputs?.load || null,
    offset: propsData
      ? (propsData.page - 1) * (specialParams?.requestLimit || resultsPerPage)
      : null,
    status: status,
    vip_status: componentData?.inputs?.vipStatus || false,
  }

  if (status === 'pending') {
    // invoice-details needs a list of invoice IDs for navigation
    params.forceAllInvoiceIds = true
    // Default invoices should all have active clients
    params.inactive_clients = false
    // Buyouts shouldn't be returned in pending
    params.is_buyout = false
    params.submitted_end_date = componentData?.inputs?.submittedEndDate
    params.submitted_start_date = componentData?.inputs?.submittedStartDate
    params.transfer_time = propsData.userIsClient
      ? null
      : componentData.apiValues.transferTime
    params.transfer_type = propsData.userIsClient
      ? null
      : componentData.apiValues.transferType

    let sort_by = 'created_at'
    let sort_direction = 'ASC'

    if (componentData?.inputs.sort === 'client') {
      sort_by = 'client_pending_total_amount'
      sort_direction = 'DESC'
    }
    else if (componentData?.inputs.sort === 'debtor') {
      sort_by = 'debtor_pending_total_amount'
      sort_direction = 'DESC'
    }

    params.sort_by = sort_by
    params.sort_direction = sort_direction

    if (componentData.inputs.general === 'verifications') {
      params.requires_verification = true
    }
    else if (componentData.inputs.general === 'flagged') {
      params.flagged = true
    }
    else if (componentData.inputs.general === 'clean invoices') {
      params.clean_invoices = true
    }
    else if (componentData.inputs.general === 'inactive clients') {
      params.inactive_clients = true
    }
  }

  if (status === 'approved') {
    let sort_by = 'approved_date'
    let sort_direction = 'ASC'

    if (componentData.inputs.sort === 'total') {
      sort_by = 'total_amount'
      sort_direction = 'DESC'
    }
    else if (componentData.inputs.sort === 'client') {
      sort_by = 'client_approved_total_amount'
      sort_direction = 'DESC'
    }
    else if (componentData.inputs.sort === 'debtor') {
      sort_by = 'debtor_approved_total_amount'
      sort_direction = 'DESC'
    }

    params.sort_by = sort_by
    params.sort_direction = sort_direction

    if (componentData.inputs.predefined === 'delayed payments') {
      const daysAgo14 = moment()
        .tz('America/New_York')
        .startOf('day')
        .subtract(14, 'days')
        .toDate()
      params.gt___payment_issue_date = daysAgo14
    }
    else if (componentData.inputs.predefined === 'unopened emails') {
      params.unopened_emails = true
    }
    else if (componentData.inputs.predefined === 'flagged') {
      params.flagged = true
    }
    else if (componentData.inputs.predefined === 'buyout') {
      params.is_buyout = true
    }

    // Add daysOustanding
    // From Paul, if we were searching for 5 to 25:
    // Days Outstanding To === 25
    // Days Outstanding From === 5
    if (
      componentData.inputs.daysOutstandingTo
      && componentData.inputs.daysOutstandingTo.length
    ) {
      // Using NY timezone (EST), toDate represents how FAR to look in the past
      // Must be passed in ISO date format
      const toDate = moment()
        .tz('America/New_York')
        .startOf('day')
        .subtract(componentData.inputs.daysOutstandingTo, 'days')
        .toDate()
      params.to_date = toDate
    }
    if (
      componentData.inputs.daysOutstandingFrom
      && componentData.inputs.daysOutstandingFrom.length
    ) {
      // Using NY timezone (EST), fromDate represents how CLOSE to look in the past
      // Must be passed in ISO date format
      const fromDate = moment()
        .tz('America/New_York')
        .startOf('day')
        .subtract((Number(componentData.inputs.daysOutstandingFrom) - 1), 'days')
        .toDate()
      params.from_date = fromDate
    }

    // Add "additional filters"
    // If all require_X are false, nothing will be passed, which is "all" additional filters
    if (componentData?.apiValues?.requireOnlineSubmit) {
      params.include_models = params.include_models || {}
      params.include_models.debtor = {
        where: {
          require_online_submit: true,
        },
      }
    }
    else if (componentData?.apiValues?.requireOriginals) {
      params.include_models = params.include_models || {}
      params.include_models.debtor = {
        where: {
          require_originals: true,
        },
      }
    }
    else if (componentData?.apiValues?.requireCopies) {
      params.include_models = params.include_models || {}
      params.include_models.debtor = {
        where: {
          require_copies: true,
        },
      }
    }

    // flag search
    if (
      componentData.inputs.flag
      && componentData.inputs.flag !== 'none'
    ) {
      params.custom_flag = componentData.inputs.flag
    }
  }

  if (status === 'paid') {
    let or___status_debtor_payment = null
    if (componentData.inputs.additionalFilter === 'overpaid') {
      or___status_debtor_payment = [ 'overpay' ]
    }
    else if (componentData.inputs.additionalFilter === 'shortpaid') {
      // a nonpayment counts as a shortpay
      or___status_debtor_payment = [ 'shortpay', 'nonpayment' ]
    }
    else if (componentData.inputs.additionalFilter === 'nonpaid') {
      or___status_debtor_payment = [ 'nonpayment' ]
    }

    params.include = 'all'
    params.load_number = null // override default load_number property
    params.like___load_number = componentData.inputs.load || null
    params.gt___paid_date = componentData.apiValues.gtDate
    // Backend doesn't like lt___ being, so pass only when absolutely needed
    params.lt___paid_date = componentData.inputs.term === 'last year'
      ? componentData.apiValues.ltDate
      : null
    params.or___status_debtor_payment = or___status_debtor_payment
    params.sort_by = 'paid_date'
    params.sort_direction = 'DESC'
  }

  if (status === 'declined') {
    params.gt___declined_date = componentData.apiValues.gtDate
    // Backend doesn't like lt___ being, so pass only when absolutely needed
    params.lt___declined_date = componentData.inputs.term === 'last year'
      ? componentData.apiValues.ltDate
      : null
    params.load_number = null // override default load_number property
    params.like___load_number = componentData.inputs.load || null
    params.sort_by = 'declined_date'
    params.sort_direction = 'DESC'
  }

  if (specialParams) {
    Object.assign(params, specialParams)
  }

  return params
}

// Max query string support is 2048 characters
// Filling every input in approved with the longest option (~350)
export const configureUrlString = (status, componentData, propsData) => {
  // Universal search doesn't have any componentData or propsData
  // but its results are returned from the same search function
  if (
    componentData.inputs
    && componentData.apiValues
    && propsData
  ) {
    return stringifyObjectToQueryString({
      accountManager: componentData.inputs.accountManager
        ? componentData.inputs.accountManager.id
        : null,
      clientID: componentData.inputs.clientID,
      daysOutstandingFrom: componentData.inputs.daysOutstandingFrom,
      daysOutstandingTo: componentData.inputs.daysOutstandingTo,
      debtorID: componentData.inputs.debtorID,
      flag: componentData.inputs.flag && componentData.inputs.flag !== 'none'
        ? componentData.inputs.flag
        : null,
      general: componentData.inputs.general.toLowerCase() !== "all invoices"
        ? componentData.inputs.general
        : null,
      invoiceID: componentData.inputs.invoiceID,
      load: componentData.inputs.load,
      page: propsData.page,
      predefined: componentData.inputs.predefined,
      query: componentData.inputs.query,
      requireCopies: componentData.apiValues.requireCopies,
      requireOriginals: componentData.apiValues.requireOriginals,
      requireOnlineSubmit: componentData.apiValues.requireOnlineSubmit,
      sort: ![ 'declined', 'paid' ].includes(status)
        ? componentData.inputs.sort
        : null,
      statusDebtorPayment: componentData.inputs.additionalFilter !== 'all'
        ? componentData.inputs.additionalFilter
        : null,
      submittedEndDate: componentData?.inputs?.submittedEndDate,
      submittedStartDate: componentData?.inputs?.submittedStartDate,
      term: componentData.inputs.term,
      transferTime: propsData.userIsClient
        ? null
        : componentData.apiValues.transferTime,
      transferType: propsData.userIsClient
        ? null
        : componentData.apiValues.transferType === 'wire'
          // https://bobtail.atlassian.net/browse/SM-1672
          ? 'expedite'
          : componentData.apiValues.transferType,
      vipStatus: componentData.inputs.vipStatus,
    })
  }
  else {
    return null
  }
}

export const formatSearchStats = (number, sum) => {
  // Sum should be a big instance
  const formattedSum = new Intl
    .NumberFormat('en-US', { style: 'currency', currency: 'USD' })
    .format(sum.div(100))

  return `${number} Total (${formattedSum.trim()})`
}

export const formatUniversalSearchIDProperty = (ids) => {
  if (ids && Array.isArray(ids) && ids.length) {
    // Used when you click "view invoices" from an invoice update
    return { in___id: [ ...ids ] }
  }
  else if (ids && (ids.length || Number.isInteger(ids))) {
    // Used when you type a ID in the select search
    return { display_id: `${ids}` }
  }
  else {
    return {}
  }
}

export const searchInvoices = async (status, componentData, propsData, specialParams = null) => {
  const searchInfo = searchPrep(status, componentData, propsData, specialParams)
  let results

  results = (await Invoice.queryList(searchInfo.searchParams, specialParams?.requestLimit || resultsPerPage)).data

  setSearchURLAndLocalStorage(status, results.all_invoice_ids, searchInfo.searchParams, searchInfo.urlString)

  return results
}

const setSearchUrl = (urlString) => {
  if (!urlString) {
    return
  }
  else {
    // Convert URL to URL representing data of request
    window.history.replaceState({}, '', `?${urlString}`)
  }
}

const setSearchLocalStorage = (status, invoiceIDs, params, urlString) => {
  if (status === 'pending') {
    // Store search in localStorage for invoice-details
    window.sessionStorage.setItem('search', JSON.stringify({
      ids: invoiceIDs,
      params,
      url: `/invoices/pending?${urlString}`,
    }))
  }
}

export const setSearchURLAndLocalStorage = (status, invoiceIDs, params, urlString) => {
  setSearchUrl(urlString)
  setSearchLocalStorage(status, invoiceIDs, params, urlString)
}

export const searchPrep = (status, componentData, propsData, specialParams = null) => {
  const searchParams = configureSearchParams(status, componentData, propsData, specialParams)
  const urlString = configureUrlString(status, componentData, propsData)
  return {
    searchParams,
    urlString,
  }
}
